import React from 'react';
import { Field as FFField } from 'react-final-form';

type FieldProps<N extends FieldName> = React.ComponentProps<typeof FFField> & { name: N; value?: FormValues[N] };

/** react-final-form Field, but it checks that the name is defined
 * in the FormValues type and the value is the corresponding type */
export function Field<N extends FieldName>(props: React.PropsWithChildren<FieldProps<N>>) {
  return <FFField {...props} />;
}

export type GenderOption = 'm' | 'f' | 'o';
export type BackgroundOption = 'ao' | 'lukio' | 'amk' | 'avoinamk' | 'yo' | 'avoinyo';
export type StudyRightOption = 'varsinainen' | 'erillinen';
export type Faculty = 'hytk' | 'itk' | 'jsbe' | 'edupsy' | 'ltk' | 'mltk';
export type StudyLevel = 'p' | 'a' | 's';
export type DegreeProgram = 'kandimaisteri' | 'maisteri' | 'chydenius';

// Some questions removed as of october 2022 (and more in 2023),
// kept as comments to remind
// why the keys aren't consistent with the order numbers
export const basicReadingLikerts = {
  perus1: '1. Olen nopea lukija',
  // perus2: '2. Lukiessani arvailen sanoja',
  // perus3: '3. Väsyn lukiessani',
  perus4: '2. Katseeni harhailee lukiessani, jolloin minun on vaikea pysyä rivillä',
  perus5: '3. Teen toistuvasti virheitä lukiessani',
  // perus6: '6. Jätän usein sanan loput lukematta',
  perus7: '4. Luen sanoja edestakaisin sahaten',
  // perus8:
  //   '8. Lukemiseen liittyvät kielteiset kokemukseni ovat niin voimakkaita, ' +
  //   'että ne vaikeuttavat selviytymistäni opiskeluun liittyvistä lukemistehtävistä',
};
export const readingLikerts = {
  luku1: '5. Lukiessani törmään usein sanoihin, joiden merkitys on minulle outo',
  luku2: '6. Lukiessani virkkeiden ymmärtäminen on minulle vaikeaa',
  luku3: '7. Kun olen lukenut kappaleen, joudun usein palaamaan sen alkuun ymmärtääkseni lukemani',
  luku4: '8. Minun on helppo koota lukemani teksti kokonaisuudeksi',
  luku5: '9. Löydän helposti lukemani tekstin ydinasiat',
};
export const englishlikerts = {
  eng1: '10. Minulla on hyvä sanavarasto englannin kielessä',
  eng2: '11. Minun on vaikea hahmottaa englannin kielen lauserakenteita',
  eng3: '12. Kun olen lukenut englanninkielisen kappaleen, joudun usein palaamaan sen alkuun ymmärtääkseni lukemani',
  eng4: '13. Minun on helppo koota englanninkielinen teksti kokonaisuudeksi',
  eng5: '14. Löydän helposti lukemastani englanninkielisestä tekstistä ydinasiat',
  // eng6: '19. Minulla on ollut vaikeuksia oppia englannin kieltä',
  // eng7:
  //   '20. Englannin kieleen liittyvät kielteiset kokemukseni ovat niin voimakkaita, ' +
  //   'että ne vaikeuttavat selviytymistäni opiskeluun liittyvistä englanninkielisten tekstien lukemistehtävistä',
};
export const digitalLikerts = {
  digi1: '15. Minun on helppo paikantaa sanat tai virkkeet digitaalisesta tekstistä',
  digi2: '16. Katseeni harhailee lukiessani digitaalista tekstiä',
  digi3: '17. Kun olen lukenut digitaalisen tekstikappaleen, joudun usein palaamaan sen alkuun ymmärtääkseni lukemani',
  digi4: '18. Minun on helppo koota lukemani digitaalinen teksti kokonaisuudeksi',
  digi5: '19. Löydän helposti lukemistani digitaalisista teksteistä ydinasiat',
  //digi6: '58. Väsyn lukiessani digitaalista tekstiä',
};
// all digital english questions removed in 2023
export const digitalEnglishLikerts = {
  // digien1: '20. Minun on helppo paikantaa sanat tai virkkeet digitaalisesta englanninkielisestä tekstistä',
  // digien2: '21. Katseeni harhailee lukiessani digitaalista englanninkielistä tekstiä',
  // digien3:
  //   '22. Kun olen lukenut digitaalisen englanninkielisen tekstikappaleen, ' +
  //   'joudun usein palaamaan sen alkuun ymmärtääkseni lukemani',
  // digien4: '23. Minun on helppo koota lukemani digitaalinen englanninkielinen teksti kokonaisuudeksi',
  // digien5: '24. Löydän helposti lukemistani digitaalisista englanninkielisistä teksteistä ydinasiat',
};
export const basicWritingLikerts = {
  kirjp1: '20. Kirjoittaessani sanoja teen usein kirjoitusvirheitä',
  kirjp2: '21. Yhdyssanojen kirjoittaminen on minulle vaikeaa',
  kirjp3: '22. Minulla on vaikeuksia virkkeiden muodostamisessa',
  kirjp4: '23. Minun on helppo kiinnittää huomiota kielioppisääntöihin samalla, kun kirjoitan',
  // kirjp5:
  //   '25. Sanojen oikeinkirjoitukseen liittyvät kielteiset kokemukseni ovat niin voimakkaita, ' +
  //   'että ne vaikeuttavat selviytymistäni opiskeluun liittyvistä kirjoitustehtävistä',
};
export const writingLikerts = {
  // kirjt1: '26. Pitkänkään tekstin kirjoittaminen ei tuota minulle ongelmia',
  // kirjt2: '27. Osaan muuttaa tekstin tyyliä tekstilajin ja lukijoiden mukaan',
  kirjt3: '24. Minun on usein vaikea päästä kirjoittamisessa alkuun',
  kirjt4: '25. Tekstin sisältöjen valinta on usein minulle vaikeaa',
  // kirjt5: '30. Osaan lukea omaa tekstiäni arvioivasti',
  // kirjt6: '31. Kirjoittamiseen keskittyminen on minulle helppoa',
  kirjt7: '26. Tekstin jäsentäminen loogiseksi tuottaa minulle vaikeuksia',
  kirjt8: '27. Minun on vaikea kirjoittaa monivaiheisesti: luonnostella, yhdistellä, muuttaa ja muokata tekstiä',
  // kirjt9:
  //   '34. Kun kirjoitan jostain lähteestä, minun on vaikea ennen kirjoittamista ymmärtää lähdetekstiä syvällisesti',
  // kirjt10: '35. Minun on helppo pyytää muilta palautetta keskeneräisistä teksteistä',
  // kirjt11: '36. Kirjoitan mieluiten yksin',
};
export const attitudeLikerts = {
  asenne1: '28. Olen mielestäni hyvä kirjoittaja',
  // asenne2:
  //   '38. Kirjoittamiseen liittyvät kielteiset kokemukseni ovat niin voimakkaita, ' +
  //   'että ne vaikeuttavat selivytymistäni opiskeluun liittyvistä kirjoitustehtävistä',
  asenne3: '29. Innostun joskus kirjoittaessani niin paljon, että en malta lopettaa',
  asenne4: '30. Minulla on selkeä käsitys omasta tavastani kirjoittaa',
  asenne5: '31. Otan mielelläni vastaan kirjoitustehtäviä',
  // asenne6: '42. Toivoisin, että opinnoissani olisi enemmän kirjoittamisen opetusta',
};
// new questions 11-15 added in 2023
export const strategyLikerts = {
  strat11: '32. Opintojakson alussa suunnittelen ajankäyttöäni ja opiskeluaikatauluani',
  strat1: '33. Minun on helppo pysyä suunnitellussa aikataulussa',
  // strat2: '44. Minun on vaikea koota luennoilla kuulemani asiat kokonaisuudeksi',
  // strat3: '45. Opetustilanteessa minun on helppo poimia tärkeät asiat muistiinpanoihin',
  // strat4: '34. Minun on vaikea löytää sopivia opiskelutapoja erilaisiin tehtäviin',
  // strat5: '35. Kun opiskelen, minun on vaikea ylläpitää keskittymistäni',
  strat12: '34. Minun on vaikea löytää aikaa opiskeluun',
  strat13: '35. Työskentelyn aloittaminen on minulle usein vaikeaa',
  strat14: '36. Kun opiskelen, minun on helppo ylläpitää keskittymistäni',
  strat6: '37. Yritän sitkeästi opetella vaikeitakin asioita',
  // strat7: '49. Pyrin tekemään tehtäväni mahdollisimman hyvin',
  // strat8: '50. Asetan opiskeluissani itselleni matalampia tavoitteita kuin mihin oikeasti pystyisin',
  // strat9: '51. Pyrin ymmärtämään opiskelemani asian syvällisesti',
  // strat10: '52. Muistan hyvin ne asiat, jotka olen opiskellut',
  strat15: '38. Minun on vaikea tehdä opiskelutehtäviäni loppuun',
};
// entirely new question set added in 2023
export const experienceLikerts = {
  tavat1: '39. Uusien asioiden opiskeleminen on minusta kiinnostavaa',
  tavat2: '40. Oman alani peruskäsitteiden ymmärtäminen on mielestäni helppoa',
  tavat3:
    '41. Ennen kuin opiskelen uuden opintojakson sisältöjä, ' +
    'silmäilen opiskelutehtäviä ja materiaaleja hahmottaakseni opintojakson tavoitteet ja tehtävät',
  tavat4: '42. Minun on vaikea löytää sopivia opiskelustrategioita erilaisiin tehtäviin',
  tavat5:
    '43. Kun opiskelen, yhdistän tietoa monesta eri lähteestä ' +
    'kuten luennoilta, kirjoista, harjoituksista tai keskusteluista',
  tavat6:
    '44. Aiemmat opiskelukokemukseni ovat niin kielteisiä, ' +
    'että ne heikentävät uskoani selviytyä opiskelutehtävistä',
  tavat7:
    '45. Aiempien opiskelukokemuksieni perusteella asetan itselleni helposti ' +
    'matalampia tavoitteita kuin mihin oikeasti pystyisin',
};
export const allLikerts = {
  ...basicReadingLikerts,
  ...readingLikerts,
  ...englishlikerts,
  ...basicWritingLikerts,
  ...writingLikerts,
  ...attitudeLikerts,
  ...strategyLikerts,
  ...digitalLikerts,
  ...digitalEnglishLikerts,
  ...experienceLikerts,
};
export type LikertName = keyof typeof allLikerts;

// these are required because radio buttons can only have string values
export type BoolString = 'true' | 'false';
export type LikertOption = '1' | '2' | '3' | '4' | '5';

export type LikertValues = Partial<Record<LikertName, LikertOption>>;

export interface OtherValues {
  userFullName: string;
  userEmail: string;
  permission1?: BoolString;
  permission2?: BoolString;
  permission3?: BoolString;
  permission4?: BoolString;
  permission5?: BoolString;
  age?: number;
  gender?: GenderOption;
  lang?: 'other' | string;
  langOther?: string;
  studyBackground?: BackgroundOption;
  studyRight?: StudyRightOption;
  faculty?: Faculty;
  major?: string;
  beginYear?: string;
  studyLevel?: StudyLevel;
  degreeProgram?: DegreeProgram;
  freeFeedback?: string;
}

export type FormValues = OtherValues & LikertValues;

export interface PreviousAnswer {
  timestamp: Date;
  values: FormValues;
  profiles: string;
}

export interface StateQueryResp {
  answers: PreviousAnswer[];
  formIsActive: boolean;
  permissionToAnswer: boolean;
}

export type FieldName = keyof FormValues;

export const initialValues: FormValues = {
  // personal info set on the app side to avoid taking them from localstorage
  userFullName: '',
  userEmail: '',
};
